import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ENV_REACT_APP } from "../Configurations/ENV_Config";
import { getToken, getAdminToken, removeToken, removeAdminToken, isTokenExpired } from "../Utils/tokenStorage";
import toast from "react-hot-toast";

// Track if we're already showing the expiry toast to prevent duplicates
let isShowingExpiryToast = false;
let isShowingRateLimitToast = false;

// Handle rate limit (429) errors
const handleRateLimitError = (errorData: any): void => {
    if (isShowingRateLimitToast) return;
    isShowingRateLimitToast = true;

    const message = errorData?.error || "Too many requests. Please wait a moment and try again.";

    toast.error(message, {
        duration: 5000,
        id: "rate-limit-error",
    });

    // Reset flag after toast duration
    setTimeout(() => {
        isShowingRateLimitToast = false;
    }, 5000);
};

const handleTokenExpiry = async (
    isAdmin: boolean,
    api: any
): Promise<void> => {
    // Prevent showing multiple toasts
    if (isShowingExpiryToast) return;
    isShowingExpiryToast = true;

    // Show toast notification
    toast.error("Your session has expired. Please login again.", {
        duration: 5000,
        id: "token-expired",
    });

    // Clear the token
    if (isAdmin) {
        removeAdminToken();
        const { logout } = await import('../Redux/admin/adminReducer');
        api.dispatch(logout());
    } else {
        removeToken();
        const { logout } = await import('../Redux/authReducer');
        api.dispatch(logout());
    }

    // Redirect after 5 seconds
    setTimeout(() => {
        isShowingExpiryToast = false;
        if (typeof window !== 'undefined') {
            window.location.href = isAdmin ? '/admin/login' : '/';
        }
    }, 5000);
};

// Auth calls still get the shared 429 handling — just never the session-expiry one.
const authCallResult = <T extends { error?: FetchBaseQueryError }>(result: T): T => {
    if (result.error && result.error.status === 429) {
        console.warn('Rate limit exceeded.');
        handleRateLimitError(result.error.data);
    }
    return result;
};

// Base fetch query for user requests
const baseQuery = fetchBaseQuery({
    baseUrl: ENV_REACT_APP?.REACT_APP_API_BASE_URL || '',
    prepareHeaders: (headers, { getState }) => {
        const token = getToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

// Base fetch query for admin requests
const adminBaseQuery = fetchBaseQuery({
    baseUrl: ENV_REACT_APP?.REACT_APP_API_BASE_URL || '',
    prepareHeaders: (headers, { getState }) => {
        const token = getAdminToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

/**
 * Sign-in and sign-up calls. A 401 from these means "wrong email or password",
 * never "your session ended": treating it as an expiry showed "Your session has
 * expired" to someone who wasn't signed in, and bounced them to the homepage
 * five seconds later, wiping the login form (Asher's dad, 2026-10-16, after
 * every attempt). They also skip the stale-token pre-check, which would
 * otherwise swallow a fresh login whenever an old expired token was still
 * stored.
 */
const AUTH_CALL_PREFIXES = ['/api/login', '/api/register', '/api/admin/login'];

export const isAuthCall = (args: string | FetchArgs): boolean => {
    const url = typeof args === 'string' ? args : args?.url || '';
    return AUTH_CALL_PREFIXES.some((p) => url === p || url.startsWith(`${p}/`) || url.startsWith(`${p}?`));
};

// Custom base query with token expiry check and 401 handling for user requests
export const customFetchBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    if (isAuthCall(args)) return authCallResult(await baseQuery(args, api, extraOptions));

    // Check token expiry before making the request
    const token = getToken();
    if (token && isTokenExpired(token)) {
        console.warn('User token expired. Logging out.');
        await handleTokenExpiry(false, api);
        return {
            error: {
                status: 401,
                data: { message: 'Token expired' },
            } as FetchBaseQueryError,
        };
    }

    const result = await baseQuery(args, api, extraOptions);

    // A 401 only means the session ended if there was a session on the request.
    if (result.error && result.error.status === 401 && token) {
        console.warn('Token expired or invalid. Logging out user.');
        await handleTokenExpiry(false, api);
    }

    // Check for 429 Rate Limit error
    if (result.error && result.error.status === 429) {
        console.warn('Rate limit exceeded.');
        handleRateLimitError(result.error.data);
    }

    return result;
};

// Custom base query with token expiry check and 401 handling for admin requests
export const addAdminTokenInHeaders: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    if (isAuthCall(args)) return authCallResult(await adminBaseQuery(args, api, extraOptions));

    // Check token expiry before making the request
    const token = getAdminToken();
    if (token && isTokenExpired(token)) {
        console.warn('Admin token expired. Logging out.');
        await handleTokenExpiry(true, api);
        return {
            error: {
                status: 401,
                data: { message: 'Token expired' },
            } as FetchBaseQueryError,
        };
    }

    const result = await adminBaseQuery(args, api, extraOptions);

    // A 401 only means the session ended if there was a session on the request.
    if (result.error && result.error.status === 401 && token) {
        console.warn('Admin token expired or invalid. Logging out admin.');
        await handleTokenExpiry(true, api);
    }

    // Check for 429 Rate Limit error
    if (result.error && result.error.status === 429) {
        console.warn('Rate limit exceeded.');
        handleRateLimitError(result.error.data);
    }

    return result;
};
